<template>
    <div class="space-y-2">
        <InlineCreateAllocationVariant
            v-if="canCreateVariant"
            :status="inlineCreateStatus"
            @click="createNewInlineVariant"
        />
        <div v-if="!loading && variantsRef.length > 0" class="space-y-2">
            <ExternalEventVariantCard
                v-for="variant in variantsRef"
                ref="variantCardCompositionRef"
                :key="variant.id"
                :external-event-variant-id="eventId"
                :category-color="getCategoryColor(category)"
                :variant="variant"
                :variants-ref="variantsRef"
                :phase-types="externalEventPhaseTypes"
                :reference-allocation-query-document="ExternalEventAllocationPhaseTileDataDocument"
                :reference-allocation-link-label="$t('phases-tile-title-reference-allocation')"
                :set-variants-ref="setVariantsRef"
                :set-current-variant-loading="setCurrentVariantLoading"
                :set-current-variant-error="setCurrentVariantError"
                :is-national="isNational"
                :link-to-reference-allocation="{
                    name: 'external-event-event-allocations-tab',
                    params: { eventId },
                }"
                @set-current-variant="(variantId) => onSetCurrentVariant(variantId)"
            />
        </div>
    </div>
</template>
<script setup lang="ts">
import auth from '@/auth/auth';
import InlineCreateAllocationVariant from '@/components/InlineCreateAllocationVariant/InlineCreateAllocationVariant.vue';
import { mapToPhaseColor } from '@/components/Timeline/PhaseColorMappings';
import ExternalEventVariantCard, {
    ExternalEventVariantCardExpose,
} from '@/external_event/components/ExternalEventVariantCard.vue';
import { useExternalEventInjection } from '@/external_event/composables/useExternalEventInjection';
import { useExternalEventVariants } from '@/external_event/composables/useExternalEventVariants';
import { getCategoryColor } from '@/external_event/externalEvent';
import {
    ExternalEventAllocationPhaseTileDataDocument,
    GetExternalEventAllocationUsagesDocument,
} from '@/generated/graphql';
import { useQuery } from '@vue/apollo-composable';
import { useTemplateRefsList } from '@vueuse/core';
import { computed, Ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const props = defineProps<{ eventId: string }>();

const emit = defineEmits<{
    (e: 'handleTabRouteLeave'): void;
}>();
const eventId = computed(() => props.eventId);

const { event } = useExternalEventInjection();
const canCreateVariant = computed(() => auth.isAdmin());

const variantCardCompositionRef: Ref<ExternalEventVariantCardExpose[]> = useTemplateRefsList();

const {
    category,
    variantsRef,
    isNational,
    loading,
    setCurrentVariantLoading,
    setCurrentVariantError,
    inlineCreateStatus,
    setVariantsRef,
    setCurrentVariant,
    createNewInlineVariant,
} = useExternalEventVariants(event, variantCardCompositionRef);

async function onSetCurrentVariant(variantId: string) {
    setCurrentVariant(variantId);
}

// as the usages do not change during a session we can use the cache here
const { result: allocationUsages } = useQuery(GetExternalEventAllocationUsagesDocument, null, {
    fetchPolicy: 'cache-first',
});

const externalEventPhaseTypes = computed(() => {
    const cat = category.value;
    return (
        allocationUsages.value?.externalEventAllocationUsages?.nodes.map((n) => ({
            ...n,
            color: cat ? mapToPhaseColor(n.type, cat) : 'External-Event-Category-External-Event',
        })) ?? []
    );
});

onBeforeRouteLeave(() => {
    emit('handleTabRouteLeave');
    return true;
});
</script>
